import { ok } from './ok';
import { err } from './err';
import type { AsyncResult, AppError } from '../types';

/**
 * @group Functions
 * @summary <blockquote>Executes an asynchronous function and returns an AsyncResult.</blockquote>
 * @typeParam T - The type of the successful value.
 * @typeParam E - The type of the error (defaults to AppError).
 * @param fn - The async function to execute.
 * @returns A Promise resolving to an Ok if successful, or an Err if rejected.
 * @example
 * ```typescript
 * const result = await tryCatchAsync(async () => {
 *   return await riskyAsyncOperation();
 * });
 * ```
 */
export async function tryCatchAsync<T, E = AppError>(fn: () => Promise<T>): AsyncResult<T, E> {
  try {
    const value = await fn();
    return ok(value);
  } catch (error) {
    return err(error as E);
  }
} 